// Third party
import moment from "moment/moment";

// Helpers
import { accountFormat } from "@Common/helpers/number";
import { MixedType, TDataProperty } from "@DataPages/types/salesOperations/salesLog/utils";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";

// Types
type CustomizedReferenceDotProps = {
	readonly cx?: number;
	readonly cy?: number;
	text: string | number;
	unitType: string;
	transformedData: MixedType[];
	isLastDayOfMonth: boolean;
	activeDay: number;

	daysInCurrentMonth: number;
	dataProperty: TDataProperty;
	chartIndex: number;
	selected: ICommissionCycle;
	lastActiveCommissionCycle: ICommissionCycle;
};

type ValidationParams = {
	activeDay: number;
	daysInCurrentMonth: number;
	isLastDayOfMonth: boolean;
	selected: ICommissionCycle | undefined;
	lastActiveCommissionCycle: ICommissionCycle | undefined;
};

/******************************************** HELPERS *****************************************************/
const getXOffsetDependTextLength = (textLength: number) => {
	switch (textLength) {
		case 2:
			return -10;
		case 3:
			return -15;
		case 4:
			return -19;
		case 5:
			return -23;
		case 6:
			return -29;
		case 7:
			return -33;
		case 8:
			return -37;
		case 9:
			return -42;
		default:
			return -47;
	}
};

const formatText = (text: string | number, unitType: string) => {
	if (accountFormat(text, 0) === "-") {
		return 0;
	} else if (unitType === "unit") {
		return text;
	} else {
		return accountFormat(text, 0);
	}
};

function formatUnitTypeText(text: string | number, unitType: string): string {
	const formattedText = formatText(text, unitType);
	return unitType === "finance" ? `$${formattedText}` : formattedText.toString();
}

const getEstimatedTextWidth = (text: string | number) => {
	const basicLengthForZero = 15;
	const properPadding = 11;
	const transformedText = Math.round(+(text || 0));
	return transformedText?.toString()?.length < 2 ? basicLengthForZero : transformedText?.toString()?.length * properPadding;
};

function isValidCondition(params: ValidationParams): boolean {
	const { activeDay, daysInCurrentMonth, isLastDayOfMonth, selected, lastActiveCommissionCycle } = params;

	return (
		activeDay === daysInCurrentMonth &&
		!isLastDayOfMonth &&
		moment(selected?.startDate).isBetween(lastActiveCommissionCycle?.startDate, lastActiveCommissionCycle?.endDate, null, "[]") &&
		!moment(selected?.endDate).isBefore(moment())
	);
}
export default function CustomizedReferenceDot(props: Readonly<CustomizedReferenceDotProps>) {
	const { cx: x = 0, cy: y = 0 } = props;
	let {
		text,
		unitType,
		transformedData,
		isLastDayOfMonth,
		activeDay,
		daysInCurrentMonth,
		dataProperty,
		chartIndex,
		selected,
		lastActiveCommissionCycle,
	} = props;

	if (isValidCondition({ activeDay, daysInCurrentMonth, isLastDayOfMonth, selected, lastActiveCommissionCycle })) {
		if (chartIndex === 1) {
			text = transformedData[moment().date() - 1]?.["summary"]?.["salesPace"];
		} else {
			text = transformedData[daysInCurrentMonth - 1]?.[dataProperty]?.[1];
		}
	}

	const currentBarValue = transformedData[activeDay - 1]?.[dataProperty] || [0, 0];
	const [first, second] = currentBarValue;
	let color;
	if (first < second) {
		color = "#2357BE";
	} else {
		color = "#B3B3B3";
	}

	const estimatedTextWidth = getEstimatedTextWidth(text); // estimated text width based on text length
	const padding = 9; // padding for each side
	const boxWidth = estimatedTextWidth + 2 * padding; // box width based on text width and padding

	const triangleWidth = 5; // Adjust this to change the width of the triangle's base
	const triangleHeight = 6; // Adjust this to change the height of the triangle

	let trianglePosition = boxWidth / 2; // default center position
	let svgXOffset = 0; // offset for the SVG container's x position

	// Adjust triangle position for the last day
	const shiftTrianglePosition = 5;
	const PROPER_SALES_GROSS_SHIFT = 11;
	const PROPER_SALES_UNIT_SHIFT = 7;
	const shiftSVGPosition = chartIndex === 0 ? PROPER_SALES_GROSS_SHIFT : PROPER_SALES_UNIT_SHIFT;
	if (activeDay === daysInCurrentMonth && text?.toString()?.split(".")[0].length > 1) {
		trianglePosition = boxWidth - shiftTrianglePosition - triangleWidth * 2; // move triangle to the right
		svgXOffset = triangleWidth * 2 + getXOffsetDependTextLength(text?.toString()?.split(".")[0].length) - shiftSVGPosition; // move SVG container to the right
	}

	const [value1, value2] = currentBarValue;
	const isIncremental = value1 <= value2;

	let pathD;
	const pathIncremental = `
		    M ${padding} ${2.5}
		    Q ${1} ${2.5 - 0.5}, ${1} ${6.25}
		    L ${1} ${28.75}
		    Q ${1} ${32.5}, ${padding - 2} ${32.5}
		    L ${trianglePosition - triangleWidth} ${32.5}
		    L ${trianglePosition} ${32.5 + triangleHeight}
		    L ${trianglePosition + triangleWidth} ${32.5}
		    L ${boxWidth - padding} ${32.5}
		    Q ${boxWidth - 2} ${32.5}, ${boxWidth - 2} ${30}
		    L ${boxWidth - 2} ${6.25}
		    Q ${boxWidth - 2} ${2.5}, ${boxWidth - padding + 2} ${2.5}
		    L ${padding} ${2.5}
		    Z
		`;

	const pathDecremental = `
			M ${padding} ${47.5}
			Q ${5} ${47}, ${5} ${44}
			L ${5} ${50 - 28.75}
			Q ${5} ${50 - 32.5}, ${padding + 2} ${50 - 32.5}
			L ${trianglePosition - triangleWidth} ${50 - 32.5}
			L ${trianglePosition} ${50 - 32.5 - triangleHeight}
			L ${trianglePosition + triangleWidth} ${50 - 32.5}
			L ${boxWidth - padding} ${50 - 32.5}
			Q ${boxWidth - 2} ${17}, ${boxWidth - 2} ${21}
			L ${boxWidth - 2} ${50 - 6.25}
			Q ${boxWidth - 2} ${50 - 2.5}, ${boxWidth - padding} ${47}
			L ${padding} ${50 - 2.5}
			Z
		`;

	let svgYOffset = 0;
	let textYPosition;
	if (isIncremental) {
		textYPosition = "20"; // original y position
		pathD = pathIncremental;
	} else {
		const offset = 28;
		textYPosition = "35"; // adjust this value as needed for upside-down alignment
		pathD = pathDecremental;
		svgYOffset = offset + (activeDay === daysInCurrentMonth ? 10 : 0); // adjust this value to move the upside-down cloud thought downward
	}

	return (
		<svg width={boxWidth} height="50" x={x - boxWidth / 2 + svgXOffset} y={y - 39 + svgYOffset}>
			<path d={pathD} fill="white" stroke={color} />
			<text
				x={boxWidth / 2}
				y={textYPosition}
				fill={color}
				textAnchor="middle"
				dominantBaseline="middle"
				fontSize="12px"
				fontWeight={"500"}
				fontFamily={"Montserrat"}
			>
				{formatUnitTypeText(text, unitType)}
			</text>
		</svg>
	);
}
